import React from 'react';
import { useTranslation } from 'react-i18next';
//import { useNavigate } from 'react-router-dom';
import { Skeleton } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { copyProjectInfo, copyProjectLimits, copyProjectRoom, HotelInfo, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';
import { FieldDescriptor, UrlInfo } from '@/features/channels/channelsSlice';
import { AppDescriptor } from '@/features/apps/appsSlice';

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import { WelField } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input'

import { CatHeader } from './Project';
import { WelcomeField } from './WelcomeWizard';

export interface EnabledAppsProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  room?:				HotelRoom;
  setRoom?:				(room: HotelRoom) => any;
}

const EnabledApps = (props: EnabledAppsProps) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const apps = useAppSelector((state) => state.apps);
  const logins = useAppSelector((state) => state.logins);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !props.project) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, props.project, props.limits]);
  const seeAllOnly = role?.privilages?.includes("SeeAllProjects") && !role.admin && !role?.privilages?.includes("EditProjectSettings") && !isProjectMember;

  React.useEffect(() => {
    store.getApps();
  }, []);
  
  const uniqueApps = React.useMemo(() => {
    const appNames: string[] = [];
    
    return apps.apps.filter((app: AppDescriptor) => {
      if (appNames.indexOf(app.name) == -1) {
        appNames.push(app.name);
        return true;
      }
    
      return false;
    });
  }, [apps.apps]);
  
  const updateRoomApps = React.useCallback((fields: UrlInfo[]) => {
	if (!props.room)
	  return;
    
	let room = copyProjectRoom(props.room);
	room.apps.apps = fields;

	if (props.setRoom)
	  props.setRoom(room);
  }, [props.room, props.setRoom]);
  
  const updateLimitsApps = React.useCallback((fields: UrlInfo[]) => {
    if (!props.limits)
	  return;
    
    let limits = copyProjectLimits(props.limits);
      
    limits.apps.apps = fields;
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);
  
  if (props.project.is_new)
    return null;
  
  const caption = 	props.room
					?	"apps:room:caption"
					:	"apps:enable_apps";
  
  const loading =	!props.project.is_new &&
			  		props.project.id != '' &&
			  		!props.limits;

  const limitsApps	=	props.limits
  						?	props.limits.apps.apps
  						:	[];

  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
    <CatHeader caption={caption} />
    {uniqueApps.map((app: AppDescriptor, i: number) => {
      const field: FieldDescriptor = {
      	field:			app.name,
      	label:			app.name,
      	placeholder:	"",
      	desc:			`Enable app '${app.name}'`,
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel", "room"],
      	acl:			false,
      	options:		[],
      };
      
      return (
      <WelcomeField key={i}
		            type=""
		            caption={field.label}
		            fields={	props.room
            					?	props.room.apps.apps
            					:	limitsApps
		            	   }
		            setFields={	props.room
	            					?	updateRoomApps
	            					:	updateLimitsApps
		            		  }
		            projFields={limitsApps}
		            limits={props.limits}
		            room={props.room}
		            isLoaded={!loading}
		            acl={false}
		            isReadOnly={seeAllOnly}
		            field={field} />
      );
    })}
  </FlexRow>
  );
}

/*export interface AppFieldProps extends EnabledAppsProps {
  app:					AppDescriptor;
  isLoaded?:			boolean;
}

export const AppField = (props: AppFieldProps) => {
  const { t } = useTranslation();
  const loaded = props.isLoaded === undefined
  				 ?	true
  				 :	props.isLoaded;
  
  const val =	props.room
  				?	props.room.apps.names.indexOf(props.app.name) > -1
  				:	(	props.limits
		  				?	props.limits.apps.names.indexOf(props.app.name) > -1
		  				:	false
		  			);
  
  const updateValue = React.useCallback((enabled: boolean) => {
    if (!props.limits)
      return;
    
    let changes = false;
    let limits = copyProjectLimits(props.limits);
    const index = limits.apps.names.indexOf(props.app.name);
    
    if (enabled) {
      if (index < 0) {
        limits.apps.names.push(props.app.name);
        changes = true;
      }
    } else
    if (index > -1) {
      limits.apps.names.splice(index, 1);
      changes = true;
    }
    
    if (changes)
      props.setLimits(limits);
  }, [props.limits, props.app]);
  
  const updateValueRoom = React.useCallback((enabled: boolean) => {
    if (	!props.room ||
        	!props.setRoom
       )
      return;
    
    let changes = false;
    let room = copyProjectRoom(props.room);
    const index = room.apps.names.indexOf(props.app.name);
    
    if (enabled) {
      if (index < 0) {
        room.apps.names.push(props.app.name);
        changes = true;
      }
    } else
    if (index > -1) {
      room.apps.names.splice(index, 1);
      changes = true;
    }
    
    if (changes)
      props.setRoom(room);
  }, [props.limits, props.room, props.app]);
  
  return (
    <WelField key={0} 
              label={props.app.name}>
      <CheckBox toggled={val}
                isLoaded={loaded}
                setToggled={props.room ? updateValueRoom : updateValue} />
    </WelField>
  );
};*/

export default EnabledApps;




